import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Phone, ShieldCheck, Sparkles, Hand, Target, Zap, Activity,
  Camera, BarChart3, Users, Stethoscope, FlaskConical, BookOpen, Beaker,
  ChevronRight, TrendingUp, Trophy, HandMetal,
} from "lucide-react";
import { loadMotor, aggregateMotor, buildDemoMotor, setHandMode, motorTimeline, motorPlan } from "@/lib/neuroMotorStore";
import CameraRecorder from "@/components/neuromotor/CameraRecorder";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const PROMPTS = {
  baseline: "Sit comfortably. Slowly raise both arms to shoulder height, then lower them. Repeat gently for 10 seconds.",
  reach: "Reach your dominant hand toward a target in front of you — for example, a cup on a table. Return to rest. Repeat.",
  reaction: "Place your hands in your lap. When you feel ready, quickly raise one hand as if catching a ball. Repeat.",
  sequence: "Touch your nose, then your opposite shoulder, then your knee. Repeat the sequence at your own pace.",
  coordination: "Slowly touch your left index finger to your right index finger in front of you. Move them apart and back together.",
  bilateral: "Combined cognitive + motor: remember this sequence — nose, opposite shoulder, opposite knee. Perform it slowly, then repeat in reverse.",
};

const TABS = [
  { key: "journey", label: "Journey", Icon: Sparkles },
  { key: "baseline", label: "Baseline", Icon: Target },
  { key: "reach", label: "Target Reach", Icon: Target },
  { key: "reaction", label: "Reaction", Icon: Zap },
  { key: "sequence", label: "Sequence", Icon: Activity },
  { key: "coordination", label: "Coordination", Icon: HandMetal },
  { key: "camera", label: "Movement Lab", Icon: Camera },
  { key: "progress", label: "Progress", Icon: BarChart3 },
  { key: "plan", label: "My Plan", Icon: TrendingUp },
  { key: "bests", label: "Personal Bests", Icon: Trophy },
  { key: "caregiver", label: "Caregiver", Icon: Users },
  { key: "therapist", label: "Professional", Icon: Stethoscope },
  { key: "research", label: "Research", Icon: FlaskConical },
  { key: "how", label: "How it works", Icon: BookOpen },
  { key: "demo", label: "2-min Demo", Icon: Beaker },
];

const Placeholder = ({ label }) => (
  <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8">
    <div className="text-[10px] uppercase tracking-widest text-orange-300 font-semibold">
      {label}
    </div>
    <p className="mt-3 text-zinc-300 max-w-2xl">
      Coming next chunk. Data is already wired into the device-only store; the
      full UI arrives in the following chunk of NeuroMotor AI.
    </p>
  </div>
);

export default function NeuroMotor() {
  const [state, setState] = useState(() => loadMotor());
  const [tab, setTab] = useState("journey");
  const [demo, setDemo] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, [tab]);

  const effective = demo ? buildDemoMotor() : state;
  const agg = useMemo(() => aggregateMotor(effective), [effective]);
  const hand = state.profile?.hand_mode || "both";

  const changeHand = (mode) => { setHandMode(mode); setState(loadMotor()); };

  return (
    <div className="min-h-screen bg-[#05050A] text-white pb-24">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 opacity-70"
        style={{ background: "radial-gradient(45% 30% at 15% 5%, rgba(251,146,60,0.18) 0%, transparent 60%), radial-gradient(40% 28% at 92% 90%, rgba(34,211,238,0.14) 0%, transparent 60%)" }}
      />

      <div className="border-b border-white/5 sticky top-0 z-30 backdrop-blur-md bg-black/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="motor-back-home" className="inline-flex items-center gap-2 text-zinc-300 hover:text-white text-sm">
            <ArrowLeft className="w-4 h-4" /> SilentScan<span className="text-cyan-400">.AI</span>
          </Link>
          <a href="tel:108" data-testid="motor-emergency-108" className="inline-flex items-center gap-1.5 rounded-full bg-red-500 text-black text-xs font-semibold px-3 py-1.5">
            <Phone className="w-3.5 h-3.5" /> Emergency 108
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pt-10">
        <div className="max-w-3xl">
          <div className="text-[10px] font-semibold uppercase tracking-[0.35em] text-orange-300/80">
            Chapter 16 · NeuroMotor AI
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.02]"
          >
            Practice movement.
            <br />
            <span className="italic text-orange-300">Measure progress.</span>
          </motion.h1>
          <p className="mt-5 text-zinc-300 max-w-xl text-lg leading-relaxed">
            An assistive digital motor-skill practice and progress-tracking
            prototype. Touchscreen-based. Non-diagnostic. Task-performance only.
          </p>

          {/* 4-pillar strip */}
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <Pill to="/dashboard" c="#22d3ee" label="SilentScan AI" />
            <ChevronRight className="w-3 h-3 text-zinc-600" />
            <Pill to="/neurorehab" c="#a78bfa" label="NeuroRehab" />
            <ChevronRight className="w-3 h-3 text-zinc-600" />
            <Pill to="/neurospeak" c="#34d399" label="NeuroSpeak" />
            <ChevronRight className="w-3 h-3 text-zinc-600" />
            <Pill to="/neuromotor" c="#fb923c" label="NeuroMotor" active />
          </div>
        </div>

        {/* Safety banner */}
        <div data-testid="motor-safety-banner" className="mt-6 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3">
          <ShieldCheck className="w-4 h-4 text-amber-300 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-amber-100 leading-relaxed">
            NeuroMotor AI is an assistive rehabilitation-support prototype for
            digital motor-skill practice and task-performance tracking. Only
            perform activities that are appropriate for your abilities. Stop
            if you experience pain, dizziness, sudden weakness or worsening
            symptoms, and seek appropriate medical advice. Not a medical
            device — does not diagnose or treat any condition.
          </div>
        </div>

        {/* Hand-mode selector */}
        <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex flex-wrap items-center gap-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold inline-flex items-center gap-1.5">
            <Hand className="w-3.5 h-3.5" /> Training hand
          </div>
          {[
            { k: "left", l: "Left hand" },
            { k: "right", l: "Right hand" },
            { k: "both", l: "Both hands" },
          ].map((o) => (
            <button
              key={o.k}
              data-testid={`motor-hand-${o.k}`}
              onClick={() => changeHand(o.k)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-widest ${
                hand === o.k
                  ? "border-orange-400 bg-orange-400/20 text-white"
                  : "border-white/10 bg-white/5 hover:bg-white/10 text-zinc-300"
              }`}
            >
              {o.l}
            </button>
          ))}
        </div>

        {/* Tabs */}
        <div className="mt-8 border-b border-white/10 -mx-6 px-6">
          <div className="flex gap-1 overflow-x-auto no-scrollbar pb-2">
            {TABS.map((t) => (
              <button
                key={t.key}
                data-testid={`motor-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1.5 flex-shrink-0 rounded-t-lg px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                  tab === t.key ? "text-white border-orange-400" : "text-zinc-400 border-transparent hover:text-white hover:border-white/20"
                }`}
              >
                <t.Icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8">
          {tab === "journey" && <JourneyPane agg={agg} demo={demo} onStartBaseline={() => setTab("baseline")} />}
          {tab === "baseline" && (
            <CameraRecorder
              kind="baseline"
              level={state.level || 1}
              prompt={PROMPTS.baseline}
              onDone={() => setState(loadMotor())}
              testId="motor-baseline"
            />
          )}
          {tab === "reach" && (
            <CameraRecorder
              kind="target-reach"
              level={state.level || 1}
              prompt={PROMPTS.reach}
              onDone={() => setState(loadMotor())}
              testId="motor-reach"
            />
          )}
          {tab === "reaction" && (
            <CameraRecorder
              kind="reaction"
              level={state.level || 1}
              prompt={PROMPTS.reaction}
              onDone={() => setState(loadMotor())}
              testId="motor-reaction"
            />
          )}
          {tab === "sequence" && (
            <CameraRecorder
              kind="sequence"
              level={state.level || 1}
              prompt={PROMPTS.sequence}
              onDone={() => setState(loadMotor())}
              testId="motor-sequence"
            />
          )}
          {tab === "coordination" && (
            <CameraRecorder
              kind="coordination"
              level={state.level || 1}
              prompt={PROMPTS.coordination}
              onDone={() => setState(loadMotor())}
              testId="motor-coordination"
            />
          )}
          {tab === "camera" && (
            <CameraRecorder
              kind="bilateral"
              level={state.level || 1}
              prompt={PROMPTS.bilateral}
              onDone={() => setState(loadMotor())}
              testId="motor-lab"
            />
          )}
          {tab === "progress" && <ProgressPane state={effective} demo={demo} />}
          {tab === "plan" && <PlanPane state={effective} onGo={(k) => setTab(k)} />}
          {tab === "bests" && <BestsPane state={effective} demo={demo} />}
          {tab === "caregiver" && <CaregiverPane state={effective} agg={agg} />}
          {tab === "therapist" && <TherapistPane state={effective} agg={agg} />}
          {tab === "research" && <ResearchPane agg={agg} />}
          {tab === "demo" && <DemoPane active={demo} onEnter={() => { setDemo(true); setTab("journey"); }} onExit={() => setDemo(false)} />}
          {tab === "how" && <HowPane />}
          {["caregiver","therapist","research"].includes(tab) ? null : null}
        </div>
      </div>
    </div>
  );
}

const Pill = ({ to, c, label, active = false }) => (
  <Link
    to={to}
    data-testid={`motor-pillar-${label.toLowerCase()}`}
    className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em]"
    style={{ borderColor: active ? c : `${c}55`, background: active ? `${c}25` : "transparent", color: active ? "#fff" : c }}
  >
    <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: c }} />
    {label}
  </Link>
);

const Stat = ({ label, val, color = "#fb923c", testId }) => (
  <div data-testid={testId} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</div>
    <div className="mt-1 font-display text-2xl font-semibold tabular-nums" style={{ color }}>{val}</div>
  </div>
);

const JourneyPane = ({ agg, demo, onStartBaseline }) => (
  <div>
    {demo && (
      <div className="mb-4 rounded-2xl border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-xs uppercase tracking-widest text-amber-200 font-semibold text-center">
        Demo data — not clinical results
      </div>
    )}

    {/* Big Baseline CTA — easy-to-tap primary action */}
    <button
      data-testid="motor-start-baseline-cta"
      onClick={onStartBaseline}
      className="w-full mb-4 rounded-3xl border border-orange-400/40 bg-gradient-to-br from-orange-500/20 via-orange-400/10 to-red-400/10 hover:from-orange-500/25 hover:to-red-400/15 p-6 sm:p-7 flex items-center justify-between gap-4 text-left transition-colors shadow-[0_0_60px_-24px_rgba(251,146,60,0.6)]"
    >
      <div className="flex items-center gap-4 min-w-0">
        <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-orange-400 text-black flex items-center justify-center">
          <Target className="w-7 h-7 sm:w-8 sm:h-8" strokeWidth={2.4} />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-orange-300 font-semibold">
            Start here
          </div>
          <div className="mt-0.5 font-display text-2xl sm:text-3xl font-semibold text-white leading-tight">
            Start Baseline Test
          </div>
          <div className="mt-1 text-sm text-zinc-300">
            Camera-based · 10–15 seconds · fully device-only
          </div>
        </div>
      </div>
      <div className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-full bg-orange-400 text-black text-sm font-semibold px-5 py-3">
        Begin <ChevronRight className="w-4 h-4" />
      </div>
    </button>

    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-orange-300 font-semibold">My motor journey</div>
          <h3 className="mt-1 font-display text-2xl sm:text-3xl font-semibold">
            Every tap. Every second. <span className="italic text-orange-300">Progress.</span>
          </h3>
        </div>
        <div className="inline-flex items-center gap-2 rounded-full border border-orange-400/30 bg-orange-400/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-orange-100">
          <Zap className="w-3.5 h-3.5" /> Level {agg.level}
        </div>
      </div>

      {agg.sessions === 0 && (
        <div className="mt-6 rounded-2xl border border-orange-400/25 bg-orange-400/5 p-5 text-sm text-orange-100">
          No sessions yet. Start with <b>Baseline</b> or open <b>2-min Demo</b>
          to see how the module tracks Motor Task Performance with simulated data.
        </div>
      )}

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat label="Sessions" val={agg.sessions} testId="motor-stat-sessions" />
        <Stat label="Tasks completed" val={agg.tasks} color="#22d3ee" testId="motor-stat-tasks" />
        <Stat label="Target accuracy" val={`${agg.accuracy}%`} color="#34d399" testId="motor-stat-accuracy" />
        <Stat label="Avg time" val={agg.avg_time_ms ? `${(agg.avg_time_ms/1000).toFixed(1)}s` : "—"} color="#a78bfa" testId="motor-stat-time" />
        <Stat label="Avg reaction" val={agg.avg_reaction_ms ? `${agg.avg_reaction_ms}ms` : "—"} color="#f472b6" testId="motor-stat-reaction" />
        <Stat label="Consistency" val={`${agg.consistency}%`} color="#67e8f9" testId="motor-stat-consistency" />
        <Stat label="Practice streak" val={`${agg.streak}d`} color="#fbbf24" testId="motor-stat-streak" />
        <Stat label="Level" val={agg.level} color="#fb923c" testId="motor-stat-level" />
      </div>

      <div className="mt-6 text-xs text-zinc-500 leading-relaxed max-w-2xl">
        <b>Motor Task Performance</b> — numbers reflect performance on the
        digital touchscreen tasks in this module only. Not a paralysis-recovery
        percentage. Clinical interpretation must be performed by a qualified
        physiotherapist or occupational therapist.
      </div>
    </div>

    <div className="mt-4 rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
      <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">Unified recovery journey</div>
      <div className="mt-4 flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-widest font-semibold">
        {[
          { l: "Prevention · SilentScan AI", c: "#22d3ee" },
          { l: "Cognition · NeuroRehab", c: "#a78bfa" },
          { l: "Communication · NeuroSpeak", c: "#34d399" },
          { l: "Motor · NeuroMotor", c: "#fb923c" },
          { l: "Progress · Unified", c: "#f472b6" },
          { l: "Professional review", c: "#f87171" },
        ].map((s, i, arr) => (
          <div key={s.l} className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5" style={{ borderColor: `${s.c}55`, background: `${s.c}15`, color: s.c }}>
              <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: s.c }} /> {s.l}
            </span>
            {i < arr.length - 1 && <span className="text-zinc-600">·</span>}
          </div>
        ))}
      </div>
    </div>
  </div>
);

const HowPane = () => (
  <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
    <div className="text-[10px] uppercase tracking-widest text-orange-300 font-semibold">How NeuroMotor works</div>
    <h3 className="mt-1 font-display text-2xl sm:text-3xl font-semibold">Touch. Measure. Adapt.</h3>
    <ol className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
      {[
        { t: "1. Baseline", b: "A short set of digital touchscreen tasks — target reach, sequence tap, reaction, coordination." },
        { t: "2. Adaptive engine", b: "Rule-based level shift on the last 8 sessions. Never invents medical treatment." },
        { t: "3. Hand modes", b: "Left / right / both — you choose. No inference about which side is affected." },
        { t: "4. Camera Lab", b: "Optional, experimental. Explicit consent, no video storage." },
        { t: "5. Progress", b: "Accuracy, task time, reaction time over sessions. Task performance only." },
        { t: "6. Safety", b: "Non-diagnostic language, always-visible Emergency 108, stop-if-pain guidance." },
      ].map((r) => (
        <li key={r.t} className="rounded-2xl border border-white/10 bg-black/30 p-4">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">{r.t}</div>
          <p className="mt-1 text-sm text-zinc-300 leading-relaxed">{r.b}</p>
        </li>
      ))}
    </ol>
  </div>
);

const DemoPane = ({ active, onEnter, onExit }) => (
  <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
    <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">2-minute impact demo</div>
    <h3 className="mt-1 font-display text-2xl sm:text-3xl font-semibold">
      From practice to <span className="italic text-orange-300">greater independence.</span>
    </h3>
    <p className="mt-3 text-sm text-zinc-300 max-w-2xl leading-relaxed">
      Populates 21 days of simulated target-reach, reaction and sequence
      sessions so you can walk a judge through the Journey dashboard.
      Clearly labelled <b>DEMO DATA — NOT CLINICAL RESULTS</b>.
    </p>
    <div className="mt-5">
      {!active ? (
        <button data-testid="motor-demo-start" onClick={onEnter} className="inline-flex items-center gap-2 rounded-full bg-amber-400 text-black font-medium px-5 py-2.5 text-sm hover:bg-amber-300">
          <Beaker className="w-4 h-4" /> Start demo
        </button>
      ) : (
        <button data-testid="motor-demo-exit" onClick={onExit} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 px-5 py-2.5 text-sm text-white">
          Exit demo
        </button>
      )}
    </div>
  </div>
);

// -----------------------------------------------------------------------
// Chunk C · Progress / Plan / Personal Bests
// -----------------------------------------------------------------------

const ProgressPane = ({ state, demo }) => {
  const timeline = useMemo(() => motorTimeline(state), [state]);
  const agg = useMemo(() => aggregateMotor(state), [state]);
  return (
    <div>
      {demo && (
        <div className="mb-4 rounded-2xl border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-xs uppercase tracking-widest text-amber-200 font-semibold text-center">
          Demo data — not clinical results
        </div>
      )}
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
        <div className="text-[10px] uppercase tracking-widest text-orange-300 font-semibold">
          Motor progress timeline
        </div>
        <h3 className="mt-1 font-display text-2xl sm:text-3xl font-semibold">
          Last 14 days of motor task performance.
        </h3>
        <p className="mt-2 text-xs text-zinc-500 max-w-2xl">
          These measurements represent performance on this digital task and
          do not establish medical recovery.
        </p>

        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-2">
          <Stat label="Sessions" val={agg.sessions} />
          <Stat label="Accuracy avg" val={`${agg.accuracy}%`} color="#34d399" />
          <Stat label="Avg time" val={agg.avg_time_ms ? `${(agg.avg_time_ms/1000).toFixed(1)}s` : "—"} color="#a78bfa" />
          <Stat label="Avg reaction" val={agg.avg_reaction_ms ? `${agg.avg_reaction_ms}ms` : "—"} color="#f472b6" />
        </div>

        <div className="mt-6 h-64 rounded-2xl border border-white/10 bg-black/30 p-4" data-testid="motor-timeline">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={timeline} margin={{ top: 6, right: 10, left: -18, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="day" stroke="rgba(255,255,255,0.35)" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis stroke="rgba(255,255,255,0.35)" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={30} />
              <Tooltip contentStyle={{ background: "#0A0A14", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12, color: "#fff" }} />
              <Line type="monotone" dataKey="accuracy" stroke="#34d399" strokeWidth={2} dot={{ r: 2 }} name="Accuracy %" />
              <Line type="monotone" dataKey="time" stroke="#a78bfa" strokeWidth={2} dot={{ r: 2 }} name="Task time (s)" />
              <Line type="monotone" dataKey="reaction" stroke="#f472b6" strokeWidth={2} dot={{ r: 2 }} name="Reaction (ms)" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-xs text-zinc-400">
          <Legend color="#34d399" label="Accuracy %" />
          <Legend color="#a78bfa" label="Task time (s)" />
          <Legend color="#f472b6" label="Reaction (ms)" />
        </div>
      </div>
    </div>
  );
};

const PlanPane = ({ state, onGo }) => {
  const plan = useMemo(() => motorPlan(state), [state]);
  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
      <div className="text-[10px] uppercase tracking-widest text-orange-300 font-semibold">
        My motor plan · personalised digital practice plan
      </div>
      <h3 className="mt-1 font-display text-2xl sm:text-3xl font-semibold">
        Today&apos;s suggested practice.
      </h3>
      <p className="mt-2 text-xs text-zinc-500 max-w-2xl">
        Not a medical treatment plan. Selected from a predefined library based
        on recent task performance. Skip anything that doesn&apos;t feel comfortable.
      </p>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
        {plan.map((p) => (
          <button
            key={p.kind}
            data-testid={`motor-plan-${p.kind}`}
            onClick={() => onGo(p.kind === "target-reach" ? "reach" : p.kind)}
            className="text-left rounded-2xl border border-orange-400/30 bg-orange-400/5 hover:bg-orange-400/10 p-5"
          >
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-orange-300 font-semibold">
                {p.label}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-white font-semibold">
                Level {p.level}
              </div>
            </div>
            <div className="mt-2 text-sm text-white leading-relaxed">{p.blurb}</div>
            <div className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-widest text-orange-200">
              Start <ChevronRight className="w-3 h-3" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const BestsPane = ({ state, demo }) => {
  const pb = state.personal_bests || {};
  const keys = Object.keys(pb);
  return (
    <div>
      {demo && (
        <div className="mb-4 rounded-2xl border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-xs uppercase tracking-widest text-amber-200 font-semibold text-center">
          Demo data — not clinical results
        </div>
      )}
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
        <div className="text-[10px] uppercase tracking-widest text-orange-300 font-semibold">
          My personal bests · task-performance only
        </div>
        <h3 className="mt-1 font-display text-2xl sm:text-3xl font-semibold">
          Your best moments.
        </h3>
        {keys.length === 0 ? (
          <p className="mt-4 text-sm text-zinc-500">
            Complete a few sessions to unlock personal bests.
          </p>
        ) : (
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {keys.map((k) => {
              const b = pb[k] || {};
              return (
                <div key={k} data-testid={`motor-pb-${k}`} className="rounded-2xl border border-white/10 bg-black/30 p-4">
                  <div className="text-[10px] uppercase tracking-widest text-orange-300 font-semibold">{k}</div>
                  <div className="mt-3 space-y-2 text-sm text-zinc-200">
                    <Row label="Highest accuracy" val={b.best_accuracy ? `${b.best_accuracy}%` : "—"} color="#34d399" />
                    <Row label="Fastest task time" val={b.fastest_ms && b.fastest_ms !== Infinity ? `${(b.fastest_ms/1000).toFixed(1)}s` : "—"} color="#a78bfa" />
                    <Row label="Fastest reaction" val={b.fastest_reaction && b.fastest_reaction !== Infinity ? `${b.fastest_reaction}ms` : "—"} color="#f472b6" />
                    <Row label="Longest sequence" val={b.longest_sequence || "—"} color="#22d3ee" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const Row = ({ label, val, color }) => (
  <div className="flex items-center justify-between border-b border-white/5 pb-1.5">
    <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</span>
    <span className="font-display text-lg font-semibold tabular-nums" style={{ color }}>{val}</span>
  </div>
);

const Legend = ({ color, label }) => (
  <div className="inline-flex items-center gap-2">
    <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: color }} />
    {label}
  </div>
);


// -----------------------------------------------------------------------
// Chunk E · Caregiver / Professional / Research panes
// -----------------------------------------------------------------------

const CaregiverPane = ({ state, agg }) => {
  const [consent, setConsent] = useState(false);
  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
      <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
        Caregiver view · consent-gated
      </div>
      <h3 className="mt-1 font-display text-2xl sm:text-3xl font-semibold">
        Share motor practice with someone you trust.
      </h3>
      <p className="mt-2 text-xs text-zinc-500 max-w-2xl">
        Caregivers can only see NeuroMotor activity after you tick the consent
        box. Sensitive details stay hidden. You can revoke access at any time.
      </p>

      <label className="mt-4 inline-flex items-center gap-2 text-sm text-zinc-200 cursor-pointer">
        <input
          type="checkbox"
          data-testid="motor-caregiver-consent"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="accent-orange-400"
        />
        I consent to sharing motor task performance with my caregiver.
      </label>

      {consent ? (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Row2 label="Sessions completed" val={agg.sessions} />
          <Row2 label="Practice streak" val={`${agg.streak} days`} />
          <Row2 label="Accuracy average" val={`${agg.accuracy}%`} />
          <Row2 label="Consistency" val={`${agg.consistency}%`} />
          <Row2 label="Avg task time" val={agg.avg_time_ms ? `${(agg.avg_time_ms/1000).toFixed(1)}s` : "—"} />
          <Row2 label="Personal bests recorded" val={Object.keys(state.personal_bests || {}).length} />
        </div>
      ) : (
        <div className="mt-5 rounded-2xl border border-white/10 bg-black/30 p-5 text-sm text-zinc-400">
          Consent required — no data will be shown.
        </div>
      )}
    </div>
  );
};

const TherapistPane = ({ state, agg }) => (
  <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
    <div className="text-[10px] uppercase tracking-widest text-red-300 font-semibold">
      Professional dashboard · PT/OT view
    </div>
    <h3 className="mt-1 font-display text-2xl sm:text-3xl font-semibold">
      Motor task history for a qualified professional.
    </h3>
    <p className="mt-2 text-xs text-zinc-500 max-w-2xl">
      Clinical interpretation must be performed by a qualified physiotherapist
      or occupational therapist. NeuroMotor does not diagnose neurological
      conditions.
    </p>

    <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-2">
      <Row2 label="Sessions" val={agg.sessions} />
      <Row2 label="Tasks" val={agg.tasks} />
      <Row2 label="Accuracy" val={`${agg.accuracy}%`} />
      <Row2 label="Reaction" val={agg.avg_reaction_ms ? `${agg.avg_reaction_ms}ms` : "—"} />
      <Row2 label="Task time" val={agg.avg_time_ms ? `${(agg.avg_time_ms/1000).toFixed(1)}s` : "—"} />
      <Row2 label="Consistency" val={`${agg.consistency}%`} />
      <Row2 label="Level" val={agg.level} />
      <Row2 label="Streak" val={`${agg.streak}d`} />
    </div>

    <div className="mt-5 rounded-2xl border border-white/10 bg-black/30 p-4">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">
        Recent 8 sessions
      </div>
      <div className="grid grid-cols-1 gap-1 text-xs text-zinc-300">
        {(state.sessions || []).slice(-8).reverse().map((x, i) => (
          <div key={i} className="flex items-center justify-between border-b border-white/5 py-1">
            <span>
              {(x.at || "").slice(0, 10)} · <span className="text-white">{x.kind}</span> · L{x.level}
            </span>
            <span className="text-orange-300">
              {x.accuracy || 0}% · {x.completion_ms ? `${(x.completion_ms/1000).toFixed(1)}s` : "—"}
              {x.reaction_ms ? ` · ${x.reaction_ms}ms` : ""}
            </span>
          </div>
        ))}
        {(state.sessions || []).length === 0 && (
          <div className="text-zinc-500">No sessions yet.</div>
        )}
      </div>
    </div>

    <div className="mt-4 flex flex-wrap gap-2">
      {["Reviewed", "Needs modification", "Not appropriate", "Review required"].map((l) => (
        <button
          key={l}
          data-testid={`motor-review-${l.toLowerCase().replace(/\s+/g, "-")}`}
          className="rounded-full border border-white/10 bg-white/5 hover:bg-white/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-zinc-300"
        >
          {l}
        </button>
      ))}
    </div>
    <p className="mt-3 text-[11px] text-zinc-500">
      Review status is stored locally on the professional&apos;s device only.
      No fabricated professional approval is stored anywhere.
    </p>
  </div>
);

const ResearchPane = ({ agg }) => (
  <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
    <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">
      Research mode · transparency
    </div>
    <h3 className="mt-1 font-display text-2xl sm:text-3xl font-semibold">
      What we know · testing · don&apos;t yet know.
    </h3>
    <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
      <div className="rounded-2xl border border-emerald-400/25 bg-emerald-400/5 p-4">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">What we know</div>
        <p className="mt-2 text-xs text-zinc-300 leading-relaxed">
          Repeated task-specific motor practice is a foundational component
          of established rehabilitation programmes. Basic touchscreen and
          camera measurements can capture consistency and completion trends.
        </p>
      </div>
      <div className="rounded-2xl border border-cyan-400/25 bg-cyan-400/5 p-4">
        <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">What we are testing</div>
        <p className="mt-2 text-xs text-zinc-300 leading-relaxed">
          Whether a personalised, self-directed digital motor-skill programme
          can produce measurable improvements on selected on-device tasks
          over repeated sessions.
        </p>
      </div>
      <div className="rounded-2xl border border-amber-400/30 bg-amber-400/5 p-4">
        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">What we don&apos;t yet know</div>
        <p className="mt-2 text-xs text-zinc-300 leading-relaxed">
          Whether on-device improvements translate to real-world function.
          Whether adherence holds over months. Whether it is safe for every
          user without professional oversight. No clinical claims.
        </p>
      </div>
    </div>

    <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-2">
      <Row2 label="Task accuracy" val={`${agg.accuracy}%`} />
      <Row2 label="Task time" val={agg.avg_time_ms ? `${(agg.avg_time_ms/1000).toFixed(1)}s` : "—"} />
      <Row2 label="Reaction" val={agg.avg_reaction_ms ? `${agg.avg_reaction_ms}ms` : "—"} />
      <Row2 label="Adherence" val={`${agg.streak}d`} />
    </div>

    <div className="mt-5 rounded-2xl border border-white/10 bg-black/30 p-4">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
        Expert review · placeholders
      </div>
      <ul className="mt-2 text-xs text-zinc-400 space-y-1.5">
        <li>· Physiotherapist review — pending</li>
        <li>· Occupational therapist review — pending</li>
        <li>· Rehabilitation specialist review — pending</li>
        <li>· Accessibility review — pending</li>
        <li>· Safety review — pending</li>
      </ul>
      <p className="mt-2 text-[11px] text-zinc-500">
        No expert names, credentials or testimonials are shown until they are
        formally provided. Prototype-grade module.
      </p>
    </div>

    {agg.sessions === 0 && (
      <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-zinc-400">
        No research data available yet.
      </div>
    )}
  </div>
);

const Row2 = ({ label, val }) => (
  <div className="rounded-2xl border border-white/10 bg-black/30 p-3">
    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</div>
    <div className="mt-1 font-display text-xl font-semibold tabular-nums text-white">{val}</div>
  </div>
);

